import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { AppState } from '../../../../reducers';
import { Account } from '../../../../account/models/account.model';
import { AccountGlobalStore } from '../../../../account/store/account-global.store';
import {
  BrandingFadeOutRequested,
  CoreActionsTypes,
} from '../../../../core/store/core.actions';
import { BrandAssetsService } from '../brand-assets.service';
import { BrandingBootstrapService } from '../branding-bootstrap.service';
import { AlternateIconService } from '../alternate-icon.plugin';
import {
  BrandSwapRequested,
  MobileBrandingActionsTypes,
} from './branding.actions';
import { DEFAULT_BRAND } from '../brands.registry';

/**
 * Effects del feature `mobile-branding`.
 *
 * Encapsula los tres responsables del flow de branding en mobile:
 *
 * 1. `accountLoadedBrandSwap$`
 *    Observa el signal `AccountGlobalStore.account` y, cuando aparece un
 *    Account (o cambia a otro openId), decide en base a
 *    `account.brandCodeAffiliations` si dispatchea `BrandSwapRequested` o si
 *    fadea el overlay sin swap.
 *
 * 2. `brandSwapRequested$`
 *    Reacciona a `BrandSwapRequested` aplicando:
 *      - Update del overlay al brand resuelto (crossfade animado).
 *      - Persistencia del brandCode en Capacitor Preferences.
 *      - Icon swap nativo (iOS inmediato, Android diferido a ON_STOP).
 *      - FadeOut del overlay para revelar la app.
 *
 * 3. `brandingFadeOutRequested$`
 *    Bridge para flujos compartidos. Cuando algún effect necesita fadear el
 *    overlay (errores tempranos del bootstrap) dispatcha
 *    `BrandingFadeOutRequested` y este effect responde. En clientes
 *    no-mobile el effect no está registrado, así que la action queda muerta
 *    sin handler (no-op silencioso).
 *
 * `BrandSwapRequested` se despacha ÚNICAMENTE desde `accountLoadedBrandSwap$`,
 * tomando el primer brandCode afiliado al account que matchee con el
 * registry. El `tenantConfig.brandCode` del start-flow (subdomain/URL)
 * NO dispara el swap, porque corresponde al tenant y no al usuario.
 * Cambios de sidedrawer tampoco despachan esta action.
 */
@Injectable()
export class MobileBrandingEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<AppState>);
  private readonly brandAssets = inject(BrandAssetsService);
  private readonly bootstrap = inject(BrandingBootstrapService);
  private readonly alternateIcon = inject(AlternateIconService);
  private readonly accountGlobalStore = inject(AccountGlobalStore);

  // NOTE: Replaces the legacy `AccountLoaded` ngrx action removed in the
  // SignalStore migration. Emits whenever `AccountGlobalStore.account`
  // transitions to a populated value, and re-emits only on openId changes
  // so the brand swap doesn't run again for the same user.
  private readonly accountLoaded$ = toObservable(
    this.accountGlobalStore.account
  ).pipe(
    filter((account): account is Account => !!account),
    distinctUntilChanged((prev, curr) => prev.openId === curr.openId)
  );

  accountLoadedBrandSwap$ = createEffect(
    () =>
      this.accountLoaded$.pipe(
        tap(async account => {
          // NOTE: To QA the icon swap without relying on the user's real
          // brandCodeAffiliations, uncomment the FORCE_BRAND_CODE block below
          // and set it to a registered brandCode (see brands.registry.ts).
          // Re-comment before shipping.
          // const FORCE_BRAND_CODE: string | null = 'goldenstatewarriors';
          // if (FORCE_BRAND_CODE) {
          //   this.store.dispatch(
          //     new BrandSwapRequested({ brandCode: FORCE_BRAND_CODE })
          //   );
          //   return;
          // }
          const affiliations = account.brandCodeAffiliations ?? [];

          const matched = affiliations.find(code =>
            this.brandAssets.isRegistered(code)
          );

          if (matched) {
            this.store.dispatch(new BrandSwapRequested({ brandCode: matched }));
            return;
          }

          // NOTE: This user has no registered brand. If a previous (branded)
          // user left a non-default brand cached in Preferences, the native
          // icon + splash logo would leak into this account (and into the
          // next cold start, since the bootstrap reads `lastBrandCode`). Reset
          // to the default brand so the swap effect clears the native icon and
          // overwrites the cache. If there's nothing stale cached, just reveal
          // the app.
          const cached = await this.bootstrap.getCachedBrandCode();
          const hasStaleBrand = !!cached && cached !== DEFAULT_BRAND.brandCode;

          if (hasStaleBrand) {
            this.store.dispatch(
              new BrandSwapRequested({ brandCode: DEFAULT_BRAND.brandCode })
            );
            return;
          }

          // NOTE: When there's no brand swap to perform, the swap effect
          // never runs, so nobody hides the boot overlay. Fade it out
          // here to reveal the app; otherwise the splash stays on top.
          void this.bootstrap.fadeOutOverlay(0);
        })
      ),
    { dispatch: false }
  );

  brandSwapRequested$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<BrandSwapRequested>(
          MobileBrandingActionsTypes.BrandSwapRequested
        ),
        tap(async ({ payload }) => {
          const brandCode = payload.brandCode;
          console.log('[BrandSwap] effect received brandCode:', brandCode);
          const entry = this.brandAssets.resolve(brandCode);
          console.log('[BrandSwap] resolved registry entry:', {
            brandCode: entry.brandCode,
            iosAlternateIconName: entry.iosAlternateIconName,
            androidAliasName: entry.androidAliasName,
            backgroundColor: entry.backgroundColor,
          });

          this.bootstrap.applyBranded(entry);

          const cached = await this.bootstrap.getCachedBrandCode();
          console.log(
            '[BrandSwap] cached brandCode:',
            cached,
            '| target:',
            entry.brandCode
          );
          if (cached !== entry.brandCode) {
            await this.bootstrap.persistBrandCode(entry.brandCode);
            console.log(
              '[BrandSwap] calling native setIcon ->',
              entry.iosAlternateIconName,
              '/',
              entry.androidAliasName
            );
            await this.alternateIcon.setIcon({
              iosName: entry.iosAlternateIconName,
              androidAlias: entry.androidAliasName,
            });
            console.log('[BrandSwap] native setIcon done');
          } else {
            console.log('[BrandSwap] brand already applied, skipping setIcon');
          }

          void this.bootstrap.fadeOutOverlay(500);
        })
      ),
    { dispatch: false }
  );

  brandingFadeOutRequested$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<BrandingFadeOutRequested>(
          CoreActionsTypes.BrandingFadeOutRequested
        ),
        tap(() => {
          void this.bootstrap.fadeOutOverlay(0);
        })
      ),
    { dispatch: false }
  );
}
